import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { TalentEditDialog } from "@/components/admin/TalentEditDialog";
import { Play, Pause, Pencil } from "lucide-react";
import placeholderTalent from "@/assets/placeholder-talent.jpg";
interface TalentCardProps {
  id: string;
  name: string;
  image: string;
  specialties: string[];
  voiceType?: string;
  talentDbId?: string;
  bio?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  displayOrder?: number;
}

const TalentCard = ({ 
  id, 
  name, 
  image, 
  specialties, 
  voiceType, 
  talentDbId,
  bio = "",
  isActive = true,
  isFeatured = false,
  displayOrder = 0,
}: TalentCardProps) => {
  const { t } = useTranslation();
  const { localizedPath } = useLocalizedPath();
  const { hasAdminAccess } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const talentData = talentDbId ? {
    id: talentDbId,
    name,
    slug: id,
    specialty: voiceType || "",
    bio,
    image_url: image,
    languages: specialties,
    is_featured: isFeatured,
    is_active: isActive,
    display_order: displayOrder,
  } : null;

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditOpen(true);
  };

  // Fetch first audio demo for this talent
  const { data: firstDemo } = useQuery({
    queryKey: ["talent-first-demo", talentDbId],
    queryFn: async () => {
      if (!talentDbId) return null;
      const { data, error } = await supabase
        .from("talent_media")
        .select("url")
        .eq("talent_id", talentDbId)
        .eq("media_type", "audio")
        .order("display_order")
        .limit(1)
        .maybeSingle();
      
      if (error) throw error;
      return data?.url || null;
    },
    enabled: !!talentDbId,
  });

  useEffect(() => {
    // Listen for other cards starting playback
    const handleStopOtherAudio = (e: CustomEvent<string>) => {
      if (e.detail !== id) {
        audioRef.current?.pause();
        setIsPlaying(false);
      }
    };

    window.addEventListener("talent-audio-play" as any, handleStopOtherAudio);
    
    return () => {
      window.removeEventListener("talent-audio-play" as any, handleStopOtherAudio);
    };
  }, [id]);

  // Cleanup audio on unmount only
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!firstDemo) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Notify other cards to stop playing
      window.dispatchEvent(new CustomEvent("talent-audio-play", { detail: id }));
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(firstDemo);
      audioRef.current.play();
      audioRef.current.onended = () => setIsPlaying(false);
      setIsPlaying(true);
    }
  };

  return (
    <Link to={localizedPath(`/talent/${id}`)} className="group block">
      <div className="relative aspect-[4/5] overflow-hidden bg-muted">
        <img
          src={image || placeholderTalent}
          alt={name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <span className="text-sm font-medium text-primary-foreground">{t("talentProfile.about")} →</span>
        </div>
        
        {/* Admin Edit Button */}
        {hasAdminAccess && talentDbId && (
          <Button
            variant="secondary"
            size="icon"
            className="absolute top-3 right-3 h-8 w-8 rounded-full shadow-lg bg-background/90 hover:bg-background text-foreground z-10"
            onClick={handleEditClick}
            aria-label={`Edit ${name}`}
          >
            <Pencil size={14} aria-hidden="true" />
          </Button>
        )}

        {/* Play Button */}
        {firstDemo && (
          <Button
            variant="secondary"
            size="icon"
            className={`absolute bottom-3 right-3 h-10 w-10 rounded-full shadow-lg transition-all z-10 ${
              isPlaying 
                ? "bg-primary text-primary-foreground" 
                : "bg-background/90 hover:bg-background text-primary"
            }`}
            onClick={handlePlayClick}
            aria-label={isPlaying ? t("accessibility.pauseDemo", { name }) : t("accessibility.playDemo", { name })}
          >
            {isPlaying ? <Pause size={18} aria-hidden="true" /> : <Play size={18} className="ml-0.5" aria-hidden="true" />}
          </Button>
        )}
      </div>
      <div className="mt-4">
        <h3 className="heading-card text-primary group-hover:text-primary/70 transition-colors">{name}</h3>
        {voiceType && (
          <p className="text-sm text-muted-foreground mt-1">{voiceType}</p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {specialties.slice(0, 3).map((specialty) => (
            <span
              key={specialty}
              className="text-xs px-2 py-1 bg-secondary text-secondary-foreground"
            >
              {t(`languages.${specialty.toLowerCase()}`, specialty)}
            </span>
          ))}
        </div>
      </div>

      {/* Edit Dialog */}
      {hasAdminAccess && talentData && (
        <TalentEditDialog
          talent={talentData}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}
    </Link>
  );
};

export default TalentCard;
